import type { ModelPlanSeries } from "@/data/plan-a/parsePlanAData"
import type { MetricScenarioSeries, SeriesPoint } from "./metricDetailsTypes"

type SliceConfig = { firstYear: number; observedEndYear: number; splitYear: number; lastYear: number }

// Slice a per-plan model series into the chart's observed → projection →
// branch shape (mirrors how the hand-authored CSV was structured):
//  - observed   [firstYear .. observedEndYear], held flat before the data starts
//  - projection [observedEndYear .. splitYear]  (the shared Plan A trunk)
//  - branch A   [splitYear .. lastYear]         (Plan A)
//  - branch D   the Plan A fork point, then Plan D after the split
// The model series may carry annual or sub-year (fractional-year) points;
// segment boundaries are linearly interpolated and every in-between model
// point is kept, so the chart draws exactly what the source publishes.
export const buildScenarioSeriesFromModel = (
  { planA, planD }: ModelPlanSeries,
  { firstYear, observedEndYear, splitYear, lastYear }: SliceConfig,
): MetricScenarioSeries => {
  const pts = [...planA].sort((a, b) => a.year - b.year)
  const valueAt = (year: number): number => {
    if (pts.length === 0) return 0
    if (year <= pts[0].year) return pts[0].value
    for (let i = 1; i < pts.length; i++) {
      if (pts[i].year >= year) {
        const a = pts[i - 1], b = pts[i]
        const f = b.year === a.year ? 0 : (year - a.year) / (b.year - a.year)
        return a.value + (b.value - a.value) * f
      }
    }
    return pts[pts.length - 1].value
  }
  const segment = (from: number, to: number): SeriesPoint[] => [
    { year: from, value: valueAt(from) },
    ...pts.filter((p) => p.year > from && p.year < to).map(({ year, value }) => ({ year, value })),
    { year: to, value: valueAt(to) },
  ]
  // The A branch ends where the model's data ends — never extrapolated flat
  // out to the axis edge.
  const branchAEnd = pts.length > 0 ? Math.min(lastYear, pts[pts.length - 1].year) : splitYear
  return {
    observed: segment(firstYear, observedEndYear),
    projection: segment(observedEndYear, splitYear),
    branches: {
      A: branchAEnd > splitYear ? segment(splitYear, branchAEnd) : [],
      D: [{ year: splitYear, value: valueAt(splitYear) }, ...planD.filter((p) => p.year > splitYear)],
    },
  }
}
